// tslint:disable:max-line-length
import { Button, Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import FocusLock from "react-focus-lock";
import { LinkButton } from "../../../core/atoms";
import { isServer } from "../../../core/detection";
import { useClickOutsideHook } from "../../../../../packages/ui/src/utils/clickOutsideHook";
import { getQueryStringValue } from "@travellocal/utils/urlHelpers";
import { RoutesInvoker } from "../../routesInvoker";
import { CurrencySelector } from "../currencySelector";
import { IMainNavMenuLink, navLinks } from "./mobileMenuHelpers";

import "./mobileMenu.scss";

/**
 * #890 Enables hiding the mobile menu overlay for the purpose of taking full page screenshots
 */
export const HIDE_MENU_FLAG = "hidemobilemenu";

interface Props {
  enquireRoute: string;
  signedInCustomer: IUser;
  mobileOverlayOpen: boolean;
  closeMenu: () => void;
  routing: RouterReducer;
}

const MobileMenuComponent = ({ enquireRoute, signedInCustomer, mobileOverlayOpen, closeMenu, routing }: Props) => {
  const [submenuActive, setSubmenuActive] = React.useState<number>(null);
  const [navigationLinks, setNavigationLinks] = React.useState<IMainNavMenuLink[]>([]);

  const triggerClose = mobileOverlayOpen && closeMenu;

  const wrapperRef = React.useRef(null);
  useClickOutsideHook(wrapperRef, triggerClose);

  const currentPath: string = routing && routing.locationBeforeTransitions
    ? (routing.locationBeforeTransitions as any).pathname
    : null;

  // Enable better fullpage screenshots
  const menuVisibility = !isServer() && getQueryStringValue(HIDE_MENU_FLAG) === "true"
    ? { display: "none" }
    : undefined;

  const hasSubmenu = (item: IMainNavMenuLink) => item && item.submenu && item.submenu.length > 0;

  const setActiveClass = (href: string, isSublink?: boolean): string => {
    const checkPath = currentPath != null
      && (
        currentPath.replace(/(\/#.*$|\/+$)/, "") === href // strips away trailing slash & url fragments
        || currentPath === href
        || (href.startsWith(RoutesInvoker.customerIndex) && currentPath.startsWith(RoutesInvoker.customerIndex))
      );

    return (classNames("mobile-menu__item",
      {
        "mobile-menu__item--active": checkPath,
        "mobile-menu__item--sub": isSublink,
      })
    );
  };

  const renderLinkWithSubMenu = (link: IMainNavMenuLink, id: number) => {
    const isActive = submenuActive === id;
    const iconClass = classNames("mobile-menu__icon", { "mobile-menu__icon--active": isActive });
    const sublistClass = classNames("mobile-menu__list", { "is-sr-only": !isActive });
    const sublistItemClass = classNames("mobile-menu__item", { "mobile-menu__item--open": isActive });

    return (
      <li key={`menu-${id}`} className={sublistItemClass}>
        <button
          className="mobile-menu__link heading--2 text--dark"
          onClick={() => setSubmenuActive(isActive ? null : id)}
          aria-haspopup="true"
          aria-expanded={isActive}
        >
          {link.text}
          <Icon className={iconClass} icon={Icon.Icomoon.arrowDown2} />
        </button>
        <ul className={sublistClass} role="menu">
          {
            link.submenu.map((sublink, index) => {
              return (
                <li key={`sub-${id + index}`} className={setActiveClass(sublink.href, true)} role="none">
                  <a className="mobile-menu__link heading--3 text--dark" href={sublink.href} tabIndex={isActive ? 0 : -1} role="menuitem">
                    &ndash;{" "}{sublink.text}
                  </a>
                </li>
              );
            },
            )
          }
        </ul>
      </li>
    );
  };

  React.useEffect(() => {
    // Listens for a page route change
    // Iterates over the defined links. Based on current path, sets the active id in react state
    const menuLinks: IMainNavMenuLink[] = navLinks(!!signedInCustomer).map((link, index: number) => {
      const linkHasSubmenu = hasSubmenu(link);

      // Iterates through sub menu to check if current path matches sub menu links
      if (linkHasSubmenu) {
        const linkItem = link.submenu.findIndex((subLink) => currentPath?.indexOf(subLink.href) !== -1);
        if (linkItem !== -1) {
          setSubmenuActive(index);
        }
      }
      return { id: index, ...link };
    });

    setNavigationLinks(menuLinks);
  }, [routing]);

  return (
    <FocusLock disabled={!mobileOverlayOpen}>
      <div ref={wrapperRef} className={classNames("mobile-menu", { "mobile-menu--open": mobileOverlayOpen })} style={menuVisibility}>
        <div className="mobile-menu__overlay">
          <div className="mobile-menu__inner">
            <div className="mobile-menu__header">
              <LinkButton
                to={!signedInCustomer ? RoutesInvoker.login() : RoutesInvoker.logout}
                buttonClass="secondary-button"
                className="is-heading text--dark"
              >
                <span>{!signedInCustomer ? "Sign In" : "Sign Out"}</span>
              </LinkButton>

              <CurrencySelector closeOnMenuClose={!mobileOverlayOpen} />
              <Button className="mobile-menu__close" onClick={closeMenu} aria-label="Close Menu" >
                <i className="kathmandu-icon icon-close" />
              </Button>
            </div>

            <nav className="mobile-menu__nav" role="navigation" aria-label="Primary Navigation">
              <ul className="mobile-menu__list" role="menu">
                {navigationLinks.map((link: IMainNavMenuLink) => {
                  return hasSubmenu(link)
                    ? renderLinkWithSubMenu(link, link.id) :
                    (
                      <li key={`menu-${link.id}`} className={setActiveClass(link.href)}>
                        <a className="mobile-menu__link heading--2 text--dark" href={link.href} role="menuitem">{link.text}</a>
                      </li>
                    );
                })}
              </ul>
              {!signedInCustomer && <LinkButton className="btn primary-button heading--4 text--light" to={enquireRoute}>Enquire</LinkButton>}
            </nav>
          </div>
        </div>
      </div>
    </FocusLock>
  );
};

MobileMenuComponent.displayName = "MobileMenu"; // Easier to find in React Dev tools with a display name

// NewHeader component (parent) re-renders on page scroll, as a result this child component re-renders.
// Memosising this component will mean it is only re-rendered when props change.
export const MobileMenu = React.memo(MobileMenuComponent);
